'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useRef } from 'react';

/** A `null` or empty value drops the key, so a cleared filter leaves no leftover in the link. */
type TQueryPatch = Record<string, string | null>;

interface IUseUrlQueryStateResult {
  /** Current value of one key, `''` when absent. */
  getParam: (key: string) => string;
  /** Merges the patch into the address bar, without reloading the page. */
  setParams: (patch: TQueryPatch) => void;
  /** Drops every key in one navigation, so "clear filters" is a single history entry. */
  clearParams: (keys: ReadonlyArray<string>) => void;
}

/**
 * The address bar AS the state: a screen reads its filters from here and writes them
 * back here, so a link already carries the filtered view and the back button works.
 * Nothing is mirrored into React state, which is what keeps the two from drifting.
 *
 * Writes go through the native `history.replaceState`, which the Next router syncs into
 * `useSearchParams`: a query change is client state, and `router.replace` turned each one
 * into a server round trip. On a page opened with a query (`?activeTab=...`), the mount
 * writes queued those round trips ahead of the person's first click, and the click never
 * landed: a tab opened by link could not be left.
 */
export function useUrlQueryState(): IUseUrlQueryStateResult {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  /**
   * The query last written and not yet reflected in `searchParams`. The write lands a render
   * later, so two writes in one handler (a filter panel applying several fields) would both
   * start from the old query and the second would undo the first.
   */
  const pendingQuery = useRef<{ base: string; next: string } | null>(null);

  const currentQuery = useCallback((): URLSearchParams => {
    const rendered = searchParams.toString();
    const pending = pendingQuery.current;
    return new URLSearchParams(pending !== null && pending.base === rendered ? pending.next : rendered);
  }, [searchParams]);

  const replaceWith = useCallback(
    (next: URLSearchParams): void => {
      const queryString = next.toString();
      pendingQuery.current = { base: searchParams.toString(), next: queryString };
      window.history.replaceState(null, '', queryString === '' ? pathname : `${pathname}?${queryString}`);
    },
    [pathname, searchParams],
  );

  const getParam = useCallback((key: string): string => searchParams.get(key) ?? '', [searchParams]);

  const setParams = useCallback(
    (patch: TQueryPatch): void => {
      const next = currentQuery();
      for (const [key, value] of Object.entries(patch)) {
        if (value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      replaceWith(next);
    },
    [currentQuery, replaceWith],
  );

  const clearParams = useCallback(
    (keys: ReadonlyArray<string>): void => {
      const next = currentQuery();
      for (const key of keys) {
        next.delete(key);
      }
      replaceWith(next);
    },
    [currentQuery, replaceWith],
  );

  return { getParam, setParams, clearParams };
}
